// Firebase Storage utilities
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Upload a file to Firebase Storage
 * @param {File|Blob} file - The file to upload
 * @param {string} path - Storage path (e.g., 'receipts/WO-123/timestamp.jpg')
 * @param {Object} metadata - Optional metadata
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadFile = async (file, path, metadata = {}) => {
  try {
    const storageRef = ref(storage, path);

    const fullMetadata = {
      contentType: file.type || 'application/octet-stream',
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        ...metadata,
      },
    };

    const snapshot = await uploadBytes(storageRef, file, fullMetadata);
    const url = await getDownloadURL(snapshot.ref);

    return {
      url,
      path: snapshot.ref.fullPath,
      metadata: snapshot.metadata,
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Upload a receipt photo
 * @param {File|Blob} file - The photo file
 * @param {string} woId - Work order ID
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadReceiptPhoto = async (file, woId) => {
  const timestamp = Date.now();
  const extension = file.type?.split('/')[1] || 'jpg';
  const path = `receipts/${woId}/${timestamp}.${extension}`;

  return uploadFile(file, path, { woId, type: 'receipt' });
};

/**
 * Upload a lock change photo
 * @param {File|Blob} file - The photo file
 * @param {string} unitId - Unit ID
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadLockPhoto = async (file, unitId) => {
  const timestamp = Date.now();
  const extension = file.type?.split('/')[1] || 'jpg';
  const path = `locks/${unitId}/${timestamp}.${extension}`;

  return uploadFile(file, path, { unitId, type: 'lock' });
};

/**
 * Upload an inspection photo
 * @param {File|Blob} file - The photo file
 * @param {string} unitId - Unit ID
 * @param {string} inspectionType - Type of inspection (fire, moveout)
 * @param {string} inspectionId - Inspection document ID
 * @returns {Promise<{url: string, path: string}>}
 */
export const uploadInspectionPhoto = async (file, unitId, inspectionType, inspectionId) => {
  const timestamp = Date.now();
  const extension = file.type?.split('/')[1] || 'jpg';
  const path = `inspections/${unitId}/${inspectionType}/${inspectionId}/${timestamp}.${extension}`;

  return uploadFile(file, path, { unitId, inspectionType, inspectionId, type: 'inspection' });
};

/**
 * Get download URL for a storage path
 * @param {string} path - Storage path
 * @returns {Promise<string>}
 */
export const getFileUrl = async (path) => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error getting file URL:', error);
    throw new Error(`Failed to get file URL: ${error.message}`);
  }
};

/**
 * List all files in a storage path
 * @param {string} path - Storage path prefix
 * @returns {Promise<Array<{name: string, path: string}>>}
 */
export const listFiles = async (path) => {
  try {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);

    return result.items.map(item => ({
      name: item.name,
      path: item.fullPath,
    }));
  } catch (error) {
    console.error('Error listing files:', error);
    throw new Error(`Failed to list files: ${error.message}`);
  }
};

/**
 * Delete a file from storage
 * @param {string} path - Storage path
 * @returns {Promise<void>}
 */
export const deleteFile = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error(`Failed to delete file: ${error.message}`);
  }
};

/**
 * Download data import file for parsing
 * @param {string} filename - Name of the file (e.g., 'work_orders.xlsx')
 * @returns {Promise<ArrayBuffer>}
 */
export const downloadDataImportFile = async (filename) => {
  try {
    const path = `data_imports/${filename}`;
    const url = await getFileUrl(path);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    return await response.arrayBuffer();
  } catch (error) {
    console.error(`Error downloading ${filename}:`, error);
    throw new Error(`Failed to download ${filename}: ${error.message}`);
  }
};

/**
 * List all data import files
 * @returns {Promise<Array<{name: string, path: string}>>}
 */
export const listDataImportFiles = async () => {
  return listFiles('data_imports');
};

// ============================================
// Insurance Estimator Storage Functions
// ============================================

/**
 * Upload an estimate document image (PDF page or screenshot)
 * @param {File|Blob|string} input - File, Blob, or base64 data URL
 * @param {string} estimateId - Estimate ID
 * @param {string} imageType - Type: 'document', 'chat', 'screenshot'
 * @param {number} pageNum - Page number (for documents)
 * @returns {Promise<{url: string, path: string, storageRef: string}>}
 */
export const uploadEstimateImage = async (input, estimateId, imageType = 'chat', pageNum = null) => {
  const timestamp = Date.now();
  const pageSuffix = pageNum ? `_page${pageNum}` : '';
  const path = `estimates/${estimateId}/${imageType}/${timestamp}${pageSuffix}.jpg`;

  let file;
  if (typeof input === 'string' && input.startsWith('data:')) {
    // Convert base64 to Blob
    const response = await fetch(input);
    file = await response.blob();
  } else {
    file = input;
  }

  const result = await uploadFile(file, path, {
    estimateId,
    imageType,
    pageNum: pageNum?.toString(),
  });

  return {
    ...result,
    storageRef: path,
  };
};

/**
 * Upload all PDF page images for an estimate
 * @param {Array<{page: number, image: string}>} pageImages - Array of page images
 * @param {string} estimateId - Estimate ID
 * @returns {Promise<Array<{page: number, url: string, storageRef: string}>>}
 */
export const uploadEstimateDocumentPages = async (pageImages, estimateId) => {
  const uploads = await Promise.all(
    pageImages.map(async ({ page, image }) => {
      const result = await uploadEstimateImage(image, estimateId, 'document', page);
      return {
        page,
        url: result.url,
        storageRef: result.storageRef,
      };
    })
  );

  console.log(`[Storage] Uploaded ${uploads.length} document pages for estimate ${estimateId}`);
  return uploads;
};

/**
 * Upload a chat image (user screenshot/attachment)
 * @param {File|Blob|string} input - File, Blob, or base64
 * @param {string} estimateId - Estimate ID
 * @returns {Promise<{url: string, storageRef: string}>}
 */
export const uploadEstimateChatImage = async (input, estimateId) => {
  const result = await uploadEstimateImage(input, estimateId, 'chat');
  console.log(`[Storage] Uploaded chat image for estimate ${estimateId}`);
  return {
    url: result.url,
    storageRef: result.storageRef,
  };
};

/**
 * Fetch an image from storage and convert to base64 for vision API
 * @param {string} url - Firebase Storage URL
 * @returns {Promise<string>} - Base64 data URL
 */
export const fetchImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error fetching image as base64:', error);
    throw new Error(`Failed to fetch image: ${error.message}`);
  }
};

/**
 * Fetch multiple images as base64 for vision API
 * @param {Array<{url: string}>} images - Array of image objects with URLs
 * @returns {Promise<Array<{url: string, base64: string}>>}
 */
export const fetchImagesAsBase64 = async (images) => {
  return Promise.all(
    images.map(async (img) => ({
      ...img,
      base64: await fetchImageAsBase64(img.url),
    }))
  );
};

export default {
  uploadFile,
  uploadReceiptPhoto,
  uploadLockPhoto,
  uploadInspectionPhoto,
  getFileUrl,
  listFiles,
  deleteFile,
  downloadDataImportFile,
  listDataImportFiles,
  // Insurance Estimator
  uploadEstimateImage,
  uploadEstimateDocumentPages,
  uploadEstimateChatImage,
  fetchImageAsBase64,
  fetchImagesAsBase64,
};
